import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/use-auth';
import { Trophy, Medal, Star, Award, Shield, ArrowUpRight, Calendar } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useLocation } from 'wouter';
import { cn } from '@/lib/utils';
import { Capacitor } from '@capacitor/core';
import { apiRequest } from '@/lib/queryClient';

interface Achievement {
  id: number;
  name: string;
  description: string;
  category: string;
  requirement: number;
  imageUrl: string;
  progress: number;
  completed: boolean;
  completedAt: string | null;
}

export function AchievementsShowcase({ userId }: { userId: number }) {
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const [selectedAchievement, setSelectedAchievement] = useState<Achievement | null>(null);
  const isOwnProfile = user?.id === userId;

  // Fetch user achievements with improved mobile handling
  const { data: achievements, isLoading, error } = useQuery({
    queryKey: ['userAchievements', userId],
    queryFn: async () => {
      try {
        // Use apiRequest from queryClient to ensure proper auth headers and base URL handling for mobile
        const res = await apiRequest('GET', `/api/achievements/user/${userId}`);
        
        if (!res.ok) {
          console.error(`Achievement fetch failed with status: ${res.status}`);
          throw new Error('Failed to fetch achievements');
        }
        
        const data = await res.json();
        
        // Log successful data retrieval for debugging
        console.log(`Fetched ${data?.length || 0} achievements for user ${userId}`);
        
        return data;
      } catch (error) {
        console.error('Achievement fetch error:', error);
        throw error;
      }
    },
    refetchOnMount: true,
    retry: 2, // Add retry logic for mobile connections
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Select the achievement to be displayed in the details panel
  useEffect(() => {
    if (achievements?.length) {
      // Initially select a completed achievement
      const completed = achievements.filter(a => a.completed);
      if (completed.length) {
        setSelectedAchievement(completed[0]);
      } else {
        setSelectedAchievement(achievements[0]);
      }
    }
  }, [achievements]);

  // Get achievements that are complete
  const completedAchievements = achievements?.filter(a => a.completed) || [];
  
  // Get achievements in progress (not completed but have some progress)
  const inProgressAchievements = achievements?.filter(a => !a.completed && a.progress > 0) || [];
  
  // Calculate overall progress
  const totalAchievements = achievements?.length || 0;
  const completedCount = completedAchievements.length;
  const progressPercentage = totalAchievements ? (completedCount / totalAchievements) * 100 : 0;

  // Get proper icon for category
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'game_milestones':
        return <Trophy className="h-4 w-4" />;
      case 'skill_based':
        return <Medal className="h-4 w-4" />;
      case 'social':
        return <Star className="h-4 w-4" />;
      case 'ai_challenge':
        return <Shield className="h-4 w-4" />;
      case 'collection':
        return <Award className="h-4 w-4" />;
      default:
        return <Trophy className="h-4 w-4" />;
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-4 mt-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-40" />
          <Skeleton className="h-8 w-24" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-32 w-full rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Progress Summary */}
      <div className="bg-black/40 backdrop-blur-md rounded-xl p-4 border border-white/5">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-primary" />
            <span className="text-lg font-semibold text-white">Achievement Progress</span>
          </div>
          <Badge variant="outline" className="bg-black/60">
            {completedCount} / {totalAchievements}
          </Badge>
        </div>
        
        <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden">
         
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progressPercentage}%` }}
            transition={{ duration: 1 }}
            className="h-full bg-primary"
          />
        </div>
        
        <div className="flex justify-between mt-2 text-xs text-white/60">
          <span>Progress</span>
          <span>{Math.round(progressPercentage)}%</span>
        </div>
      </div>

      {/* Achievements Grid */}
      <div className="space-y-4">
        {/* Section Title */}
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Medal className="h-5 w-5 text-primary" />
            Recent Achievements
          </h3>
          <Button 
            variant="ghost" 
            className="text-white/70 hover:text-white"
            onClick={() => navigate('/achievements')}
          >
            View All <ArrowUpRight className="ml-1 h-4 w-4" />
          </Button>
        </div>

        {/* Achievement Cards */}
        {completedAchievements.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {completedAchievements.slice(0, 6).map((achievement) => (
              <motion.div
                key={achievement.id}
                whileHover={{ scale: 1.02 }}
                className={cn(
                  "cursor-pointer rounded-xl overflow-hidden",
                  "border border-white/5 transition-all duration-200",
                  selectedAchievement?.id === achievement.id 
                    ? "bg-gradient-to-r from-primary/20 to-primary/5 border-primary/30" 
                    : "bg-black/40 hover:bg-black/50"
                )}
                onClick={() => setSelectedAchievement(achievement)}
              >
                <div className="p-4 flex gap-3">
                  <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-black/40 flex items-center justify-center overflow-hidden">
                    <img 
                      src={achievement.imageUrl || '/achievements/default.png'} 
                      alt={achievement.name}
                      className="object-cover w-full h-full"
                    />
                  </div>
                  <div>
                    <h4 className="font-medium text-white text-sm mb-1">{achievement.name}</h4>
                    <p className="text-xs text-white/60 line-clamp-2">{achievement.description}</p>
                    <div className="flex items-center gap-1 mt-1">
                      <Trophy className="h-3 w-3 text-yellow-400" />
                      <span className="text-xs text-yellow-400">Completed</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="bg-black/30 rounded-xl border border-white/5 p-8 text-center">
            <Trophy className="h-12 w-12 mx-auto opacity-20 mb-3" />
            <p className="text-white/70">No achievements completed yet.</p>
            {isOwnProfile && (
              <p className="text-sm text-white/50 mt-2">
                Play more games to earn achievements!
              </p>
            )}
          </div>
        )}

        {/* In Progress Achievements */}
        {inProgressAchievements.length > 0 && (
          <>
            <h3 className="text-lg font-semibold text-white flex items-center gap-2 mt-8">
              <Award className="h-5 w-5 text-primary" />
              In Progress
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {inProgressAchievements.slice(0, 3).map((achievement) => (
                <motion.div
                  key={achievement.id}
                  whileHover={{ scale: 1.02 }}
                  className={cn(
                    "cursor-pointer rounded-xl overflow-hidden",
                    "border border-white/5 transition-all duration-200",
                    selectedAchievement?.id === achievement.id 
                      ? "bg-gradient-to-r from-primary/20 to-primary/5 border-primary/30" 
                      : "bg-black/40 hover:bg-black/50"
                  )}
                  onClick={() => setSelectedAchievement(achievement)}
                >
                  <div className="p-4 flex gap-3">
                    <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-black/40 flex items-center justify-center overflow-hidden">
                      <img 
                        src={achievement.imageUrl || '/achievements/default.png'} 
                        alt={achievement.name}
                        className="object-cover w-full h-full"
                      />
                    </div>
                    <div className="w-full">
                      <h4 className="font-medium text-white text-sm mb-1">{achievement.name}</h4>
                      <p className="text-xs text-white/60 mb-2 line-clamp-1">{achievement.description}</p>
                      <div className="w-full h-1.5 bg-gray-800 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${(achievement.progress / achievement.requirement) * 100}%` }}
                          className="h-full bg-primary"
                          style={{ backgroundColor: "var(--primary)" }}
                        />
                      </div>
                      <div className="flex justify-between mt-1 text-xs">
                        <span className="text-white/60">Progress</span>
                        <span className="text-primary">{achievement.progress} / {achievement.requirement}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Selected Achievement Detail */}
      {selectedAchievement && (
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
            {getCategoryIcon(selectedAchievement.category)}
            Achievement Details
          </h3>
          <Card className="bg-black/30 backdrop-blur-md border border-white/10 overflow-hidden">
            <div className="p-6">
              <div className="flex gap-6 items-center">
                <div className="w-24 h-24 rounded-xl bg-black/40 p-2 flex items-center justify-center">
                  <img
                    src={selectedAchievement.imageUrl || '/achievements/default.png'}
                    alt={selectedAchievement.name}
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="text-xl font-bold text-white">{selectedAchievement.name}</h2>
                    <Badge 
                      variant="outline" 
                      className={cn(
                        "text-xs py-0 h-5",
                        selectedAchievement.completed 
                          ? "bg-green-950/50 text-green-400 border-green-800/50" 
                          : "bg-yellow-950/50 text-yellow-400 border-yellow-800/50"
                      )}
                    >
                      {selectedAchievement.completed ? 'Completed' : 'In Progress'}
                    </Badge>
                  </div>
                  <p className="text-white/70 mb-2">{selectedAchievement.description}</p>
                  
                  <div className="flex flex-wrap gap-2 mt-3">
                    <Badge className="bg-black/60 text-white/80 hover:bg-black/80">
                      {getCategoryIcon(selectedAchievement.category)}
                      <span className="ml-1">
                        {selectedAchievement.category.split('_').map(word => 
                          word.charAt(0).toUpperCase() + word.slice(1)
                        ).join(' ')}
                      </span>
                    </Badge>
                    
                    {selectedAchievement.completed && selectedAchievement.completedAt && (
                      <Badge variant="outline" className="text-white/70 bg-black/40 border-white/10">
                        <Calendar className="mr-1 h-3 w-3" />
                        Completed on {new Date(selectedAchievement.completedAt).toLocaleDateString()}
                      </Badge>
                    )}
                  </div>
                  
                  {!selectedAchievement.completed && (
                    <div className="mt-4">
                      <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${(selectedAchievement.progress / selectedAchievement.requirement) * 100}%` }}
                          className="h-full bg-primary"
                          style={{ backgroundColor: "var(--primary)" }}
                        />
                      </div>
                      <div className="flex justify-between mt-1 text-xs">
                        <span className="text-white/60">Progress</span>
                        <span className="text-primary">{selectedAchievement.progress} / {selectedAchievement.requirement}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}